import {
  LOYALTY_COMPARISONS,
  LOYALTY_FILTER_KEYS,
  LOYALTY_PERIODS,
  LOYALTY_PROGRAMS,
  LOYALTY_TREND_GRAINS,
  LOYALTY_TREND_VIEWS,
} from './types';
import type { LoyaltyFilterKey, LoyaltyFilters, LoyaltyPeriod, LoyaltyViewState } from './types';

const PARAM = {
  program: 'loyaltyProgram',
  period: 'loyaltyPeriod',
  from: 'loyaltyFrom',
  to: 'loyaltyTo',
  comparison: 'loyaltyCompare',
  comparisonFrom: 'loyaltyCompareFrom',
  comparisonTo: 'loyaltyCompareTo',
  trendGrain: 'loyaltyGrain',
  focusMetric: 'loyaltyFocus',
  breakdown: 'loyaltyBreakdown',
  columns: 'loyaltyCols',
  trendView: 'loyaltyView',
} as const;

const FILTER_PARAM: Record<LoyaltyFilterKey, string> = {
  tier: 'loyaltyTier',
  country: 'loyaltyCountry',
  brand: 'loyaltyBrand',
  category: 'loyaltyCategory',
  autoRenew: 'loyaltyAutoRenew',
  promo: 'loyaltyPromo',
};

// Read-only: links written before the period menu carried 7, 28 or 90 days in this key.
const LEGACY_DAYS_PARAM = 'loyaltyDays';
const LEGACY_DAYS: Record<string, LoyaltyPeriod> = {
  '7': 'last_7_days',
  '28': 'last_28_days',
  '90': 'last_90_days',
};

/** Every query param the Loyalty page owns, so the shell can clear them when another page opens. */
export const LOYALTY_PARAM_KEYS: ReadonlyArray<string> = [
  ...Object.values(PARAM),
  ...Object.values(FILTER_PARAM),
  LEGACY_DAYS_PARAM,
];

export const LOYALTY_PERIOD_OPTIONS: ReadonlyArray<{ id: LoyaltyPeriod; label: string }> = [
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'week_to_date', label: 'Week to date' },
  { id: 'month_to_date', label: 'Month to date' },
  { id: 'quarter_to_date', label: 'Quarter to date' },
  { id: 'year_to_date', label: 'Year to date' },
  { id: 'last_7_days', label: 'Last 7 days' },
  { id: 'last_14_days', label: 'Last 14 days' },
  { id: 'last_28_days', label: 'Last 28 days' },
  { id: 'last_30_days', label: 'Last 30 days' },
  { id: 'last_90_days', label: 'Last 90 days' },
  { id: 'last_12_months', label: 'Last 12 months' },
  { id: 'custom', label: 'Custom range…' },
];

const DEFAULT_STATE: LoyaltyViewState = {
  program: 'societe',
  period: 'month_to_date',
  comparison: 'previous_year',
  trendGrain: 'day',
  trendView: 'trend',
};

export function defaultLoyaltyState(): LoyaltyViewState {
  return { ...DEFAULT_STATE };
}

function oneOf<T extends string>(value: string | null, values: ReadonlyArray<T>, fallback: T): T {
  return value && values.includes(value as T) ? (value as T) : fallback;
}

function decodeFilters(params: URLSearchParams): LoyaltyFilters | undefined {
  const filters: LoyaltyFilters = {};
  for (const key of LOYALTY_FILTER_KEYS) {
    const values = [
      ...new Set(
        (params.get(FILTER_PARAM[key]) ?? '')
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean),
      ),
    ];
    if (values.length) filters[key] = values;
  }
  return Object.keys(filters).length ? filters : undefined;
}

export function decodeLoyaltyState(search: string | URLSearchParams): LoyaltyViewState {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  const legacyPeriod = LEGACY_DAYS[params.get(LEGACY_DAYS_PARAM) ?? ''];
  const requestedPeriod = oneOf(params.get(PARAM.period), LOYALTY_PERIODS, legacyPeriod ?? DEFAULT_STATE.period);
  const from = params.get(PARAM.from) || undefined;
  const to = params.get(PARAM.to) || undefined;
  const customPeriod = requestedPeriod === 'custom' && from && to;
  const period =
    requestedPeriod === 'custom' && !customPeriod ? (legacyPeriod ?? DEFAULT_STATE.period) : requestedPeriod;

  const requestedComparison = oneOf(params.get(PARAM.comparison), LOYALTY_COMPARISONS, DEFAULT_STATE.comparison);
  const comparisonFrom = params.get(PARAM.comparisonFrom) || undefined;
  const comparisonTo = params.get(PARAM.comparisonTo) || undefined;
  const customComparison = requestedComparison === 'custom' && comparisonFrom && comparisonTo;
  const comparison =
    requestedComparison === 'custom' && !customComparison ? DEFAULT_STATE.comparison : requestedComparison;

  const filters = decodeFilters(params);
  const columns = [
    ...new Set(
      (params.get(PARAM.columns) ?? '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean),
    ),
  ];
  return {
    program: oneOf(params.get(PARAM.program), LOYALTY_PROGRAMS, DEFAULT_STATE.program),
    period,
    from: customPeriod ? from : undefined,
    to: customPeriod ? to : undefined,
    comparison,
    comparisonFrom: customComparison ? comparisonFrom : undefined,
    comparisonTo: customComparison ? comparisonTo : undefined,
    trendGrain: oneOf(params.get(PARAM.trendGrain), LOYALTY_TREND_GRAINS, DEFAULT_STATE.trendGrain),
    focusMetric: params.get(PARAM.focusMetric) || undefined,
    breakdown: params.get(PARAM.breakdown) || undefined,
    ...(columns.length ? { columns } : {}),
    trendView: oneOf(params.get(PARAM.trendView), LOYALTY_TREND_VIEWS, DEFAULT_STATE.trendView),
    ...(filters ? { filters } : {}),
  };
}

/** Writes only non-default values, and leaves keys owned by other pages untouched. */
export function encodeLoyaltyState(state: LoyaltyViewState, current?: string | URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(current);
  const write = (key: string, value?: string) => (value ? next.set(key, value) : next.delete(key));
  const nonDefault = <T extends string>(value: T, fallback: T) => (value === fallback ? undefined : value);

  next.delete(LEGACY_DAYS_PARAM);
  write(PARAM.program, nonDefault(state.program, DEFAULT_STATE.program));
  write(PARAM.period, nonDefault(state.period, DEFAULT_STATE.period));
  write(PARAM.from, state.period === 'custom' ? state.from : undefined);
  write(PARAM.to, state.period === 'custom' ? state.to : undefined);
  write(PARAM.comparison, nonDefault(state.comparison, DEFAULT_STATE.comparison));
  write(PARAM.comparisonFrom, state.comparison === 'custom' ? state.comparisonFrom : undefined);
  write(PARAM.comparisonTo, state.comparison === 'custom' ? state.comparisonTo : undefined);
  write(PARAM.trendGrain, nonDefault(state.trendGrain, DEFAULT_STATE.trendGrain));
  write(PARAM.focusMetric, state.focusMetric);
  write(PARAM.breakdown, state.breakdown);
  write(PARAM.columns, state.columns?.join(','));
  write(PARAM.trendView, nonDefault(state.trendView, DEFAULT_STATE.trendView));
  for (const key of LOYALTY_FILTER_KEYS) {
    write(FILTER_PARAM[key], state.filters?.[key]?.join(','));
  }
  return next;
}
